// components/reports/SalesReport.jsx
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Badge } from '../../../../components/ui/badge';
import { 
  Search, 
  Filter, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Users,
  ShoppingBag,
  CreditCard,
  Package,
  Calendar,
  Clock,
  MapPin
} from 'lucide-react';
import { EnhancedBarChart } from '../../components/charts/EnhancedBarChart';
import { EnhancedLineChart } from '../../components/charts/EnhancedLineChart';
import { RadialProgressChart } from '../../components/charts/RadialProgressChart';

export function SalesReport({ data, orderData, onExport, onViewDetails }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  if (!data || !orderData) return null;

  // Process data for charts
  const processSalesData = () => {
    const paidOrders = orderData.filter(order => order.OrderStatus === 'Paid');
    
    // Daily sales
    const dailySales = {};
    paidOrders.forEach(order => {
      const date = new Date(order.OrderDateTime).toLocaleDateString('en-US', { weekday: 'short' });
      if (!dailySales[date]) dailySales[date] = 0;
      dailySales[date] += order.NetAmount;
    });

    // Hourly sales
    const hourlySales = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}:00`,
      sales: 0
    }));
    paidOrders.forEach(order => {
      const hour = new Date(order.OrderDateTime).getHours();
      hourlySales[hour].sales += order.NetAmount;
    });

    // Payment methods
    const paymentMethods = {
      CASH: { value: 0, count: 0 },
      CARD: { value: 0, count: 0 }
    };
    paidOrders.forEach(order => {
      const method = order.PaymentMode;
      if (method && paymentMethods[method]) {
        paymentMethods[method].value += order.NetAmount;
        paymentMethods[method].count++;
      }
    });

    // Order modes
    const orderModes = {};
    paidOrders.forEach(order => {
      const mode = order.OrderMode;
      if (!orderModes[mode]) orderModes[mode] = { value: 0, count: 0 };
      orderModes[mode].value += order.NetAmount;
      orderModes[mode].count++;
    });

    return {
      dailySales: Object.entries(dailySales).map(([day, sales]) => ({ name: day, value: sales })),
      hourlySales,
      paymentMethods: Object.entries(paymentMethods).map(([method, data]) => ({ 
        name: method, 
        value: data.value,
        count: data.count
      })),
      orderModes: Object.entries(orderModes).map(([mode, data]) => ({
        name: mode,
        value: data.value,
        count: data.count
      })),
      totalSales: paidOrders.reduce((sum, order) => sum + order.NetAmount, 0),
      totalOrders: paidOrders.length,
      avgOrderValue: paidOrders.length > 0 ? paidOrders.reduce((sum, order) => sum + order.NetAmount, 0) / paidOrders.length : 0
    };
  };

  const salesData = processSalesData();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sales</p>
                <p className="text-2xl font-bold">
                  RS {salesData.totalSales.toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+12.5%</span>
              <span className="text-sm text-muted-foreground ml-2">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
                <p className="text-2xl font-bold">{salesData.totalOrders}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <ShoppingBag className="h-6 w-6 text-blue-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+8.2%</span>
              <span className="text-sm text-muted-foreground ml-2">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Order Value</p>
                <p className="text-2xl font-bold">
                  Rs {salesData.avgOrderValue.toFixed(0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+5.3%</span>
              <span className="text-sm text-muted-foreground ml-2">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold">94.7%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+2.1%</span>
              <span className="text-sm text-muted-foreground ml-2">vs last week</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <EnhancedLineChart
          data={salesData.dailySales}
          title="Daily Sales Trend"
          lines={[
            { key: 'value', color: '#3b82f6', name: 'Sales' }
          ]}
          showArea={true}
        />
        
        <EnhancedBarChart
          data={salesData.paymentMethods}
          title="Payment Method Distribution"
          color="#10b981"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Peak Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {salesData.hourlySales.slice(10, 22).map((hour, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                      hour.sales > 5000 ? 'bg-red-500/10' : hour.sales > 3000 ? 'bg-yellow-500/10' : 'bg-green-500/10'
                    }`}>
                      <span className={`text-sm font-medium ${
                        hour.sales > 5000 ? 'text-red-500' : hour.sales > 3000 ? 'text-yellow-500' : 'text-green-500'
                      }`}>
                        {hour.hour.split(':')[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{hour.hour}</p>
                      <p className="text-sm text-muted-foreground">
                        {hour.sales > 5000 ? 'Peak' : hour.sales > 3000 ? 'Busy' : 'Normal'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">Rs {hour.sales.toLocaleString()}</p>
                    <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary"
                        style={{ width: `${Math.min((hour.sales / 10000) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Modes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {salesData.orderModes.map((mode, index) => {
                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: colors[index] || '#6b7280' }}
                        />
                        <span className="font-medium">{mode.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">RS {mode.value.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">{mode.count} orders</p>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full"
                        style={{ 
                          backgroundColor: colors[index] || '#6b7280',
                          width: `${Math.min((mode.value / salesData.totalSales) * 100, 100)}%`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Day-wise Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                const dayData = salesData.dailySales.find(d => d.name === day);
                const sales = dayData?.value || 0;
                const isToday = day === new Date().toLocaleDateString('en-US', { weekday: 'short' });
                
                return (
                  <div key={index} className={`p-4 rounded-lg border ${isToday ? 'border-primary bg-primary/5' : ''}`}>
                    <div className="text-center">
                      <p className="text-sm font-medium">{day}</p>
                      <p className="text-2xl font-bold mt-2">Rs {sales.toLocaleString()}</p>
                      <div className="flex items-center justify-center mt-2">
                        {sales > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`text-xs ml-1 ${sales > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {sales > 0 ? '+15%' : '-5%'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}